import { useTranslation } from 'next-i18next';
import {
  ChangeEvent, Dispatch, SetStateAction,
  useState, useCallback,
} from 'react';
import { MirrorData } from '@type/job-add';
import { useDebounce } from '@hooks/useDebounce/useDebounce';
import { useSwrJobSuggestedSalary } from '@hooks/useSwrApi/endpoints/job';

/**
 * @description Extracts min and max salary from a string
 *
 * @param minKey The key for the min salary
 * @param maxKey The key for the max salary
 * @param salary The salary string
 */
const extractSalary = (
  minKey: string,
  maxKey: string,
  salary = '0',
) => {
  const salaryParts = salary.split('-').map((part) => part.trim());
  const firstVal = parseInt(salaryParts[0], 10);
  const secondVal = salaryParts[1] ? parseInt(salaryParts[1], 10) : firstVal;

  return {
    [minKey]: Number.isNaN(firstVal) ? 0 : firstVal,
    [maxKey]: Number.isNaN(secondVal) ? 0 : secondVal,
  };
};

/** UseJobSalary Props */
interface UseJobSalaryProps {
  locale: Locale,
  setFormValues: Dispatch<SetStateAction<object>>,
  apiSalaryMin?: number,
  apiSalaryMax?: number,
  salaryMinInputName: string,
  salaryMaxInputName: string,
  salaryChangeOkCallback?: (salaryObject?: SalaryObject) => void,
  reqMirrorData?: Pick<MirrorData, 'title' | 'addresses' | 'description' | 'careerLevels' | 'employmentTypes' | 'workPlacement'>,
  hasInitialSalary?: boolean,
  hasInitialConfidential?: boolean,
}

/** Salary Object Interface */
export interface SalaryObject {
  salaryMin: number,
  salaryMax: number,
}

/**
 * @description Custom hook for handling salary input. Check for errors and set form values.
 * - `locale` - the current app locale
 * - `setFormValues` - setFormValues function from useBjForm
 * - `apiSalaryMin` - initial min salary value from API
 * - `apiSalaryMax` - initial max salary value from API
 * - `salaryMinInputName` - name of the min input
 * - `salaryMaxInputName` - name of the max input
 * - `salaryChangeOkCallback` - callback function to be called when salary change is ok
 * - `reqMirrorData`: The required form mirror data.
 * - `hasInitialSalary` - if the job has an initial salary
 * - `hasInitialConfidential` - if the job has an initial confidential state
 *
 * ** Returns **
 * - `hasCustomError` - custom error boolean state
 * - `customErrorMessage` - custom error message
 * - `handleSalaryChange` - salary change handler
 * - `salaryObject` - salary object with `min` and `max` values
 * - `addSuggestedSalary` - function to add a suggested salary
 * - `jobSuggestedSalary` - the job suggested salary
 * - `isJobSuggestedSalaryLoading` - if the job suggested salary is loading
 * - `userChangedSalary` - user changed salary boolean state
 * - `setUserChangedSalary` - set user changed salary function
 * - `showConfidentialityWarning` - show confidentiality warning boolean state
 * - `setShowConfidentialityWarning` - set show confidentiality warning function
 */
export const useJobSalary = (props: UseJobSalaryProps) => {
  // Destructure props
  const {
    locale,
    setFormValues,
    apiSalaryMin = 0,
    apiSalaryMax = 0,
    salaryMinInputName,
    salaryMaxInputName,
    salaryChangeOkCallback,
    reqMirrorData,
    hasInitialSalary,
    hasInitialConfidential,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Helper states for manual change of salary and confidențiality checkbox tick
  const [userChangedSalary, setUserChangedSalary] = useState<boolean>(!!hasInitialSalary);
  const [showConfidentialityWarning, setShowConfidentialityWarning] = useState<boolean>(!!hasInitialConfidential);

  // Salary custom errors states
  // **************************
  const [hasCustomError, setHasCustomError] = useState<boolean | undefined>(undefined);
  const [customErrorMessage, setCustomErrorMessage] = useState<string>('');
  const [salaryObject, setSalaryObject] = useState<SalaryObject>({ salaryMin: apiSalaryMin, salaryMax: apiSalaryMax });

  // Set salary error messages
  // **************************
  const setSalaryErrorMessage = (
    element: HTMLInputElement,
    // anything but an empty string will be considered a custom error
    validityType: string,
    errorMessage: string,
    hasError: boolean,
  ) => {
    element.setCustomValidity(validityType);
    setCustomErrorMessage(t(errorMessage));
    setHasCustomError(hasError);
  };

  // Handle input salary change. Check for errors and set form values.
  // **************************
  const handleSalaryChange = useDebounce((
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const salary = extractSalary(salaryMinInputName, salaryMaxInputName, event.target.value);
    const element = event.target as HTMLInputElement;

    if (salary[salaryMinInputName] < 305) {
      // Handle minimum value for salary
      setSalaryErrorMessage(element, 'customError', 'add.job.too-small-salary-min', true);
    } else if (salary[salaryMinInputName] > salary[salaryMaxInputName]) {
      // Handle min salary greater than max salary
      setSalaryErrorMessage(element, 'customError', 'add.job.too-small-salary-max', true);
    } else if (salary[salaryMaxInputName] > 2 * salary[salaryMinInputName]) {
      // Handle mx salary bigger than twice the min salary
      setSalaryErrorMessage(element, 'customError', 'add.job.too_high_salary_max', true);
    } else {
      // No errors
      setSalaryErrorMessage(element, '', '', false);
    }

    // Set the values for the hidden inputs
    // Convert the salaries to strings2, because the hidden inputs are strings
    setFormValues({
      [salaryMinInputName]: String(salary[salaryMinInputName]),
      [salaryMaxInputName]: String(salary[salaryMaxInputName]),
    });

    // Set salary object
    setSalaryObject({
      salaryMin: salary[salaryMinInputName] ?? 0,
      salaryMax: salary[salaryMaxInputName] ?? 0,
    });

    // Callback function
    if (!hasCustomError && salaryChangeOkCallback) {
      salaryChangeOkCallback({
        salaryMin: salary[salaryMinInputName],
        salaryMax: salary[salaryMaxInputName],
      });
    }
  });

  // Add suggested salary
  // **************************
  const addSuggestedSalary = useCallback((salary?: ApiSchema<'SalarySuggestion'>) => {
    if (!salary || Object.keys(salary).length === 0) return;
    // Take care of bad setState error. Help React queue the state update.
    setTimeout(() => {
      setFormValues({
        salary: salary && salary?.min ? `${salary.min} - ${salary?.max ?? 0}` : '',
        [salaryMinInputName]: String(salary?.min ?? 0),
        [salaryMaxInputName]: String(salary?.max ?? 0),
      });
      setSalaryObject({
        salaryMin: salary?.min ?? 0,
        salaryMax: salary?.max ?? 0,
      });
    }, 50);
  }, [salaryMinInputName, salaryMaxInputName, setFormValues]);

  // SWR, get job suggested salary
  // **************************
  const {
    data: jobSuggestedSalary,
    isValidating: isJobSuggestedSalaryLoading,
  } = useSwrJobSuggestedSalary({
    locale,
    shouldFetch: !!reqMirrorData?.title && !!reqMirrorData?.addresses,
    payload: {
      title: reqMirrorData?.title,
      locations: reqMirrorData?.addresses,
      ...(reqMirrorData?.description && { description: reqMirrorData?.description }),
      ...(reqMirrorData?.careerLevels && { careerLevels: reqMirrorData?.careerLevels }),
      ...(reqMirrorData?.employmentTypes && { employmentTypes: reqMirrorData?.employmentTypes }),
      ...(reqMirrorData?.workPlacement && { workPlacement: reqMirrorData?.workPlacement }),
    },
    onDataChanged: (currentData) => {
      // If we got a salary suggestion, and the salary was not changed manually, we add it
      if (!userChangedSalary && currentData) {
        addSuggestedSalary(currentData);
      }
    },
  });

  // Return
  // **************************
  return {
    hasCustomError,
    customErrorMessage,
    handleSalaryChange,
    salaryObject,
    addSuggestedSalary,
    jobSuggestedSalary,
    isJobSuggestedSalaryLoading,
    userChangedSalary,
    setUserChangedSalary,
    showConfidentialityWarning,
    setShowConfidentialityWarning,
  };
};
